<script setup>
import { computed } from 'vue';

import { HrIconBtn } from '../primitives';

/**
 * Standard icon button for general actions.
 *
 * @status recommended
 * @group actions
 * @useWhen Triggering a secondary action that can be represented by a single icon
 * @avoidWhen The action is destructive — use HrIconDestructiveBtn
 * @related HrIconDestructiveBtn
 * @related HrIconMenuBtn
 * @related HrIconToggleBtn
 * @related HrIconHintBtn
 * @a11y Icon-only — callers must supply aria-label describing the action.
 */
defineOptions({ name: 'HrIconActionBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /**
   * Material icon name.
   * @example thumb_up
   */
  icon: { type: String, required: true },
  /** Shows a loading spinner in place of the icon. */
  loading: { type: Boolean, default: false },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: { type: Boolean, default: false },
  /**
   * The action cannot be taken right now and the button says why, in its tooltip or aria-label.
   * Dimmed and inert like `disable`, but still hoverable and focusable so the reason is reachable.
   */
  unavailable: { type: Boolean, default: false },
  /** Renders a skeleton placeholder instead of the button. */
  pending: { type: Boolean, default: false },
  /**
   * Size of the button container and icon.
   * @values xs, sm, md, lg, xl
   */
  size: { type: String, default: 'md' },
  /** Vue Router route location. Renders the button as a router-link. */
  to: { type: [String, Object], default: null },
  /** Replace the current history entry instead of pushing a new one. */
  replace: { type: Boolean, default: false },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: { type: String, default: null },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: { type: String, default: null },
  /**
   * Quasar visual props forwarded to the underlying icon button. Ignored in the design-system theme.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: { type: Object, default: () => ({}) },
});

// @todo Remove legacy-props when Legacy theme is removed
const mergedLegacyProps = computed(() => ({
  flat: true,
  color: 'text-dark-variant',
  ...props.legacyProps,
}));
</script>

<template>
  <hr-icon-btn
    variant="standard"
    v-bind="$props"
    :legacy-props="mergedLegacyProps"
    @click="emit('click', $event)"
  >
    <!-- @slot Overlay content placed inside the button — e.g. a tooltip, badge, or menu trigger. -->
    <slot />
  </hr-icon-btn>
</template>
